import React from 'react';
import { Icon } from './Icons.jsx';
import { formatPercent, formatValue } from '../lib/format.js';

function ConservationRow({ title, metric, unit }) {
  if (!metric) return null;
  const modeLabel = metric.mode === 'conserved' ? '理论守恒' : '耗散过程';
  const defectPct = Math.min(100, (metric.defect / Math.max(metric.criticalTolerance, 1e-12)) * 100);
  const status = metric.severity;
  return (
    <div className={`conservation-row ${status}`}>
      <div className="conservation-row-top">
        <div>
          <strong>{title}</strong>
          <span className={`conservation-mode ${metric.mode}`}>{modeLabel}</span>
        </div>
        <span className={`conservation-status ${status}`}>
          {status === 'ok' ? '通过' : status === 'warning' ? '接近阈值' : '超限'}
        </span>
      </div>
      <div className="conservation-values">
        <span>
          当前 <b>{formatValue(metric.current, 3)}</b> {unit}
        </span>
        <span>
          基线 <b>{formatValue(metric.reference, 3)}</b> {unit}
        </span>
        <span className={metric.defect > 0 ? 'delta' : ''}>
          Δ {formatPercent(metric.defect, 3)}
        </span>
      </div>
      <div className="meter">
        <div className="meter-fill" style={{ width: `${Math.max(1.5, defectPct)}%` }} />
        <div className="meter-threshold" style={{ left: `${(metric.tolerance / metric.criticalTolerance) * 100}%` }} />
      </div>
    </div>
  );
}

export function DataPanel({ model, snapshot, selfTest, onRunSelfTest, onOpenDiagnostics }) {
  const { metrics, conservation } = snapshot;
  const energy = metrics.energy;
  const maxEnergy = Math.max(energy.total, energy.kinetic, energy.potential, 1e-9);
  const kineticPct = (energy.kinetic / maxEnergy) * 100;
  const potentialPct = (energy.potential / maxEnergy) * 100;
  const health =
    conservation.level === 'critical'
      ? { tone: 'danger', label: '守恒误差超限', icon: 'alert' }
      : conservation.level === 'warning'
        ? { tone: 'warning', label: '守恒误差接近阈值', icon: 'alert' }
        : { tone: 'ok', label: '守恒校验通过', icon: 'check' };

  return (
    <div className="panel-scroll data-panel">
      <div className="panel-intro">
        <div className="eyebrow">
          <Icon name="activity" size={14} />
          实时数据
        </div>
        <div className="data-title-row">
          <h2>动力学读数</h2>
          <span className="status-pill">
            <span className="status-dot" />
            {metrics.status?.label}
          </span>
        </div>
        <p>{metrics.status?.detail}</p>
      </div>

      <div className="metric-grid">
        {metrics.readouts.map((item) => (
          <div className="metric-card" key={item.key}>
            <span>{item.label}</span>
            <div>
              <strong>{formatValue(item.value, item.precision ?? 2)}</strong>
              <em>{item.unit}</em>
            </div>
            {item.hint && <small>{item.hint}</small>}
          </div>
        ))}
      </div>

      <section className="data-section">
        <div className="section-title">
          <span>能量构成</span>
          <b>{formatValue(energy.total, 3)} J</b>
        </div>
        <div className="energy-stack">
          <div className="energy-segment kinetic" style={{ width: `${kineticPct}%` }} />
          <div className="energy-segment potential" style={{ width: `${potentialPct}%` }} />
        </div>
        <div className="energy-legend">
          <span>
            <i className="dot kinetic" />
            动能 {formatValue(energy.kinetic, 3)} J
          </span>
          <span>
            <i className="dot potential" />
            势能 {formatValue(energy.potential, 3)} J
          </span>
          <span className="energy-total">
            合计 <b>{formatValue(energy.total, 3)} J</b>
          </span>
        </div>
      </section>

      {metrics.momentum && (
        <section className="data-section momentum-card">
          <div className="section-title">
            <span>系统总动量</span>
            <b>{formatValue(metrics.momentum.total, 3)} kg·m/s</b>
          </div>
          <div className="momentum-scale">
            <span className="momentum-zero">0</span>
            <div className="momentum-track">
              <div
                className="momentum-pin"
                style={{
                  left: `${Math.min(96, Math.max(4, 50 + (metrics.momentum.total / 12) * 40))}%`,
                }}
              />
            </div>
            <span className="momentum-direction">→ 正方向</span>
          </div>
          <small>碰撞过程中水平方向无外力，总动量应保持不变。</small>
        </section>
      )}

      <section className={`conservation-card-panel ${health.tone}`}>
        <div className="conservation-head">
          <div className={`health-icon ${health.tone}`}>
            <Icon name={health.icon} size={18} />
          </div>
          <div>
            <strong>{health.label}</strong>
            <small>
              步长 {(1 / snapshot.fixedDt).toFixed(0)} Hz · 自愈 {snapshot.repairs} 次
            </small>
          </div>
          <button type="button" className="text-btn" onClick={onOpenDiagnostics}>
            诊断
            <Icon name="chevronRight" size={14} />
          </button>
        </div>
        <ConservationRow title="机械能" metric={conservation.energy} unit="J" />
        <ConservationRow title="总动量" metric={conservation.momentum} unit="kg·m/s" />
        {!conservation.energy && !conservation.momentum && (
          <p className="muted-copy">当前模型不监测该守恒量。</p>
        )}
      </section>

      <section className="data-section">
        <div className="section-title">
          <span>内置守恒自检</span>
          <b className={selfTest?.summary.ok ? 'ok-text' : 'warn-text'}>
            {selfTest ? `${selfTest.summary.passed}/${selfTest.summary.total} 通过` : '未运行'}
          </b>
        </div>
        <div className="selftest-dots">
          {(selfTest?.results ?? []).map((item) => (
            <span
              key={item.name}
              className={`selftest-dot ${item.passed ? 'ok' : 'fail'}`}
              title={`${item.name}：${item.passed ? '通过' : '未通过'} · ${item.detail}`}
            />
          ))}
        </div>
        <p className="muted-copy">
          在浏览器内实时积分四类模型，校验机械能/动量守恒与参数护栏，失败时自动触发稳定性修复。
        </p>
        <button type="button" className="secondary-btn" onClick={onRunSelfTest}>
          <Icon name="shield" size={16} />
          运行内置自检
        </button>
      </section>

      <section className="data-section formula-section">
        <div className="section-title">
          <span>核心公式</span>
          <b>{model.meta.grade.split('·')[1]?.trim()}</b>
        </div>
        {model.meta.formulas.map((formula) => (
          <div className="formula-row" key={formula.label}>
            <div>
              <span>{formula.label}</span>
              <small>{formula.note}</small>
            </div>
            <code>{formula.latex}</code>
          </div>
        ))}
      </section>

      <section className="data-section brief-section">
        <div className="section-title">
          <span>实验目的与步骤</span>
          <b>{model.meta.duration}</b>
        </div>
        <ul className="objective-list">
          {model.meta.objectives.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <details className="brief-details">
          <summary>
            查看课堂操作步骤
            <Icon name="chevron" size={14} />
          </summary>
          <ol>
            {model.meta.procedure.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ol>
        </details>
      </section>

      <div className="data-footnote">
        <Icon name="cpu" size={14} />
        数据由确定性物理内核计算 · 相对误差以初始状态为基线 · 当前参数 {model.schema.length} 项
      </div>
    </div>
  );
}
